'use client';

import coinWhite from '@/assets/images/icons/coin-white.png';
import IconTextUnit from '@/components/ui/IconTextUnit/IconTextUnit';
import { LOTTERY_STATUS } from '@/constants';
import { Format } from '@/lib/dateFns';
import { swal } from '@/lib/sweetalert';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { diffDateTimeHours, newDate } from '@/utils/date';
import { goMainOneTimeAuth } from '@/utils/functions';
import { type LotteryEventDetail, formatCurrency, formatDateCommon } from '@monorepo/common';
import { isAfter, isBefore } from 'date-fns';
import type { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import LotteryDrawButton from '../LotteryDrawButton/LotteryDrawButton';
import styles from './LotteryFab.module.scss';

function LotteryFab({
  lotteryEvent,
  session = null,
}: {
  lotteryEvent: LotteryEventDetail;
  session?: Session | null;
}) {
  const [timeLeft, setTimeLeft] = useState('');
  const router = useRouter();
  useEffect(() => {
    const endAtDate = newDate(lotteryEvent.endAt);
    const interval = window.setInterval(() => {
      const currentDate = newDate();
      const diff = diffDateTimeHours(endAtDate, currentDate);
      setTimeLeft(`${diff.days}日${diff.hours}時間${diff.minutes}分${diff.seconds}秒`);
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [lotteryEvent.endAt]);

  const goLotteryConfirm = () => {
    if (!session?.user) {
      router.push(`/auth/signin?callbackUrl=/lotteries/${lotteryEvent.id}/confirm`);
      return;
    }
    router.push(`/lotteries/${lotteryEvent.id}/confirm`);
  };

  return (
    <div className={styles.componentStyle}>
      <div className={styles.container}>
        <div>
          <div className={`${styles.itemCoin} ${utilsStyles.whiteColor}`}>
            <IconTextUnit text={formatCurrency(lotteryEvent.drawFee)} imgSrc={coinWhite.src} unit={'/回'} />
            <p className={utilsStyles.textXSmall}>
              {hasShippingFee(lotteryEvent) ? '（配送手数料を含まない）' : '（配送手数料を含む）'}
            </p>
            {isPeriodLottery(lotteryEvent.startAt, lotteryEvent.endAt) && (
              <p className={styles.timeLeft}>終了まで{timeLeft}</p>
            )}
          </div>
        </div>
        {rightArea(session, lotteryEvent, goLotteryConfirm)}
      </div>
    </div>
  );
}

function isPeriodLottery(startAt: string, endAt: string) {
  return endAt && nowLotteryStatus(startAt, endAt) === LOTTERY_STATUS.is_session.value;
}

function nowLotteryStatus(startAt: string, endAt: string) {
  if (endAt && isAfter(newDate(), newDate(endAt))) {
    return LOTTERY_STATUS.later.value;
  }
  if (isBefore(newDate(), newDate(startAt))) {
    return LOTTERY_STATUS.upcoming.value;
  }
  return LOTTERY_STATUS.is_session.value;
}

function requireAddress(session: Session | null) {
  return session?.user && !session.user.setAddress;
}

function rightArea(session: Session | null, lotteryEvent: LotteryEventDetail, goLotteryConfirm: () => void) {
  const handleCustomPopup = async () => {
    const title = lotteryEvent.customOptions?.popUpMessageTitleLotteryEventEntry;
    const text = lotteryEvent.customOptions?.popUpMessageTextLotteryEventEntry;

    if (!title && !text) {
      goLotteryConfirm();
      return;
    }

    const { isConfirmed } = await swal.confirm({
      title,
      text,
      confirmButtonText: 'OK',
    });

    if (!isConfirmed) {
      return;
    }

    goLotteryConfirm();
  };
  switch (nowLotteryStatus(lotteryEvent.startAt, lotteryEvent.endAt)) {
    case LOTTERY_STATUS.upcoming.value:
      // 開催予定
      return (
        <div className={styles.upcomingButton}>
          <LotteryDrawButton disabled>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textXSmall}>
                {formatDateCommon(lotteryEvent.startAt, Format.dateTimeLotteryJa)}
              </p>
              <p className={utilsStyles.textSmall}>スタート</p>
            </div>
          </LotteryDrawButton>
        </div>
      );
    case LOTTERY_STATUS.later.value:
      return <div className={styles.endTextWrapper}>このくじは終了しています</div>;
    default:
      // 住所入力必須
      return requireAddress(session) ? (
        <div className={styles.requireAdressButton}>
          <LotteryDrawButton color={'primary'} onClick={openAddressAlert}>
            <div className={utilsStyles.centeredVertical}>
              <p className={utilsStyles.textSmall}>くじを引く前に</p>
              <p className={utilsStyles.textXSmall}>配送先情報を入力してください</p>
            </div>
          </LotteryDrawButton>
        </div>
      ) : (
        <LotteryDrawButton color={'primary'} onClick={handleCustomPopup}>
          くじイベントに参加する
        </LotteryDrawButton>
      );
  }
}

async function openAddressAlert() {
  const { isConfirmed } = await swal.confirm({
    title: '配送先情報が未入力です',
    text: 'くじを引く前に配送先情報の入力が必要です',
    confirmButtonText: '配送先登録画面へ',
  });
  if (isConfirmed) {
    goMainOneTimeAuth({
      redirectPath: `/account/address?callback=${location.href}`,
    });
  }
}

// 送料があるか
function hasShippingFee(event: LotteryEventDetail) {
  return event.shippingFee > 0 || event.additionalShippingFee > 0;
}

export default LotteryFab;
